/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Store,
  ShoppingBag,
  Package,
  Users,
  Settings,
  Menu,
  X,
  Search,
  Bell,
  User,
  Calendar,
  StoreIcon,
  Tag,
  Truck,
  DollarSign,
} from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useOrders } from '@/contexts/OrderContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'react-toastify';
import axiosInstance from '@/utils/axiosInstance';
import { API_URL, USER_ROLES } from '@/constant/constant';

const VendorDashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser } = useUser();
  const { orderCount } = useOrders();

  // Check if user is an event vendor
  const isEventVendor =
    user?.attributeValues?.find((attr) => attr.name === 'isEventVendor')?.value === 'true';

  // Load initial online status from user data
  useEffect(() => {
    if (user?.status) {
      const isVendorOnline = user.status === 'Approved';
      setIsOnline(isVendorOnline);
      localStorage.setItem(`vendor_online_status_${user?.id}`, isVendorOnline.toString());
    } else {
      // Fallback to localStorage if user status is not available
      const savedStatus = localStorage.getItem(`vendor_online_status_${user?.id}`);
      if (savedStatus !== null) {
        setIsOnline(savedStatus === 'true');
      }
    }
  }, [user?.id, user?.status]);

  const handleToggleOnlineStatus = async () => {
    if (isToggling) return;

    setIsToggling(true);
    const newStatus = !isOnline;
    const statusValue = newStatus ? 'Approved' : 'OutOfStation';

    try {
      // Update the backend using the correct API endpoint
      await axiosInstance.put(
        `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/status?vendorId=${user?.id}&status=${statusValue}`,
        {},
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            'X-USER-ROLE': USER_ROLES?.VENDOR,
          },
        }
      );

      // Update local state
      setIsOnline(newStatus);
      localStorage.setItem(`vendor_online_status_${user?.id}`, newStatus.toString());

      // Refresh user context to sync with profile page
      const phone = localStorage.getItem('contactNo');
      if (phone) {
        await refreshUser(phone, true);
      }
    } catch (error) {
      console.error('Failed to update online status:', error);
      toast.error('Failed to update status. Please try again.');
    } finally {
      setIsToggling(false);
    }
  };

  const navigationItems = [
    {
      name: 'Orders',
      href: '/',
      icon: ShoppingBag,
      current: location.pathname === '/',
      badge: orderCount > 0 ? orderCount : null,
    },
    {
      name: 'Products',
      href: '/product/pricing',
      icon: Package,
      current: location.pathname === '/product/pricing',
    },
    {
      name: 'Coupons',
      href: '/coupons',
      icon: Tag,
      current: location.pathname === '/coupons',
    },
    {
      name: 'Delivery Slabs',
      href: '/delivery-slabs',
      icon: Truck,
      current: location.pathname === '/delivery-slabs',
    },
    {
      name: 'Charges',
      href: '/charges',
      icon: DollarSign,
      current: location.pathname === '/charges',
    },

    ...(isEventVendor
      ? [
          {
            name: 'Dashboard',
            href: '/events',
            icon: Calendar,
            current: location.pathname === '/events',
          },
        ]
      : []),
    {
      name: 'Profile',
      href: '/vendor/profile',
      icon: StoreIcon,
      current: location.pathname.startsWith('/vendor/profile'),
    },
    {
      name: 'Settings',
      href: '/profile',
      icon: Settings,
      current: location.pathname.startsWith('/profile'),
    },
  ];

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 shadow-lg transform transition-transform duration-300 ease-in-out md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-start flex-1">
              <img
                src="/images/biglocallogo3.webp"
                alt="Biglocal - India's local App"
                className="h-8 w-auto object-contain"
              />
            </div>
            <button
              onClick={toggleSidebar}
              className="md:hidden text-gray-600 hover:bg-gray-200 p-1.5 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 px-4 py-3 space-y-2 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
                    item.current
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md shadow-orange-200'
                      : 'text-gray-700 hover:bg-gray-100 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon
                      className={`w-5 h-5 ${item.current ? '' : 'group-hover:scale-110 transition-transform'}`}
                    />
                    <span>{item.name}</span>
                  </div>
                  {item.badge && (
                    <Badge
                      variant="secondary"
                      className={`${
                        item.current
                          ? 'bg-white text-orange-600 hover:bg-white font-semibold'
                          : 'bg-orange-100 text-orange-700 hover:bg-orange-100'
                      }`}
                    >
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Vendor Info Footer */}
          <div className="border-t border-gray-200 p-4 bg-gradient-to-br from-gray-50 to-white">
            {/* Vendor Info */}
            <div className="flex items-center gap-3 mb-3">
              {user?.profileImage ? (
                <Avatar className="w-10 h-10 border-2 border-orange-200 shadow-sm">
                  <AvatarImage src={user.profileImage} alt={user.name || 'User'} />
                  <AvatarFallback className="bg-gradient-to-br from-orange-400 to-orange-600 text-white text-sm font-semibold">
                    {user.name ? user.name.charAt(0).toUpperCase() : 'V'}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className="flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-600 rounded-full w-10 h-10 shadow-sm">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 font-semibold text-sm truncate">{user?.shopName}</p>
              </div>
            </div>

            {/* Compact Online Status Toggle */}
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="relative flex-shrink-0">
                  <div
                    className={`w-2.5 h-2.5 rounded-full ${
                      isOnline ? 'bg-green-500 animate-pulse' : 'bg-orange-500'
                    }`}
                  ></div>
                  {isOnline && (
                    <div className="absolute inset-0 w-2.5 h-2.5 bg-green-400 rounded-full animate-ping"></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-xs font-bold leading-tight ${
                      isOnline ? 'text-green-600' : 'text-orange-600'
                    }`}
                  >
                    {isOnline ? 'Live' : 'Offline'}
                  </p>
                </div>
              </div>
              <Switch
                checked={isOnline}
                onCheckedChange={handleToggleOnlineStatus}
                disabled={isToggling}
                className="data-[state=checked]:bg-green-500"
              />
            </div>

            {/* Profile Link */}
            <button
              onClick={() => {
                navigate('/profile');
                setSidebarOpen(false);
              }}
              className="w-full mt-3 text-center text-xs text-gray-500 hover:text-orange-600 transition-colors font-medium py-2 hover:bg-orange-50 rounded-lg"
            >
              Manage in profile →
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="md:ml-64 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm backdrop-blur-sm bg-white/95">
          <div className="flex items-center justify-between px-4 py-3">
            {/* Left: Mobile menu + Breadcrumb */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSidebar}
                className="md:hidden text-gray-600 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-lg transition-colors"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-gray-900">Vendor Dashboard</span>
              </div>
            </div>

            {/* Right: Search, Notifications, Profile */}
            <div className="flex items-center gap-3">
              {/* Admin/Profile Button */}
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center justify-center gap-2 p-2.5 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-md hover:shadow-lg transition-all duration-200"
                title="View Profile"
              >
                <User className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
};

export default VendorDashboardLayout;
